import { fromValue, map, pipe } from 'wonka';
import { getEnvironment, getOptions } from '../environment';
import { addGlobalCallback, beacon, beaconImage } from '../io';
import { log, logError } from '../logger';
import { Tag } from '../tag';
import {
  createIframeSrcdoc,
  getMarkupTemplates,
  isIframe,
  loadScript,
} from '../utils/dom';
import { deepMerge } from '../utils/object';
import { insertAtSubstring, quoteToApostrophe } from '../utils/string';
import {
  buster,
  getHeight,
  processMacros,
  toBoolean,
  waitUntil,
} from '../utils/util';
import { GDPR_PREFIX } from './gdpr';
import { getEventsServerEndpoint } from '../creative';

type TemplateCallback = {
  type: 'load' | 'error' | 'timeout';
  stack?: Error['stack'];
};

/**
 * When adding our creative template (Celtra) to the page, we use a timer just in case something blocks it and
 * the onload/onerror handlers never fire
 */
const TMPL_TIMEOUT_MS = 5000;

/**
 * Regex for testing whether a line starts with a URL. This is used for detecting scripts in custom markup.
 */
const RE_URL_TEST = /^\s*https?\:\/\//i;

/**
 * These are magic tags which we'll look for in markup (typically from rtb) which may contain JSON clicktracker data.
 */
const TAG_CREATIVE_DATA_START =
  '<div class="mobkoi-creative-data" style="display:none">';
const TAG_CREATIVE_DATA_END = '</div>';

const getClickUrl = (clickUrl: string, macros: Record<string, unknown>) =>
  processMacros(macros, RE_URL_TEST.test(clickUrl) ? clickUrl : '');

/**
 * Extract and parse the JSON from our magic tag in HTML (typically from an RTB creative).
 * Or undefined if none found.
 * @param {String} string
 * @returns {Object}
 */
export const extractCreativeData = (
  string: String,
):
  | {
      clickUrl: string;
      clickUrlType: 'pixel' | 'script';
    }
  | undefined => {
  try {
    let l = string.indexOf(TAG_CREATIVE_DATA_START);
    if (l >= 0) {
      l += TAG_CREATIVE_DATA_START.length;
      const r = string.indexOf(TAG_CREATIVE_DATA_END, l);
      if (r > 0) {
        const d = string.substring(l, r);
        return JSON.parse(d);
      }
    }
  } catch (x) {}
  return;
};

export const getGDPRConsentMacros = (
  consentString: string,
  consentIds: (string | number)[] = [],
) =>
  [...new Set(consentIds)].reduce<Record<string, string>>(
    (acc, id) => ({
      ...acc,
      [`${GDPR_PREFIX}${encodeURIComponent(btoa(`\${GDPR_CONSENT_${id}}`))}`]:
        consentString,
    }),
    {},
  );

export const getCeltraMacros = (tag: Tag) => {
  const environment = getEnvironment();
  const endpoint = getEventsServerEndpoint();

  const { mobkoi, creative, impressionId } = tag.payload!;
  const {
    constraints,
    macros = {},
    clickUrl,
    gdpr_consent,
    gdpr,
    coppa,
  } = tag.pageOptions;

  const celtraMacros = {
    isTagservice: true,
    externalAdServer: 'DCM' /* MOAT tags delivered by Celtra use DCM macros */,
    externalSessionId: impressionId,
    externalSiteId: mobkoi.publisherId,
    externalSupplierName: environment.domain,
    externalPlacementId: mobkoi.placementId,
    externalCreativeId: mobkoi.adId,
    externalLineItemId: mobkoi.bookingId,
    externalCampaignId: mobkoi.campaignId,
    externalLineItemName: mobkoi.lineItemId,
    externalVideoTrackerURI: mobkoi.celtraVideoTracker.replace(
      '{{eventsServerEndpoint}}',
      endpoint,
    ),
    removeAdvertisementBars: Number(constraints.removeAdvertisementBars),
    offsetTop: getHeight(constraints.offsetTop, 0),
    offsetBottom: getHeight(constraints.offsetBottom, 0),
    'user.autoclose': toBoolean(constraints.autoclose),
    'user.disableautoplay': toBoolean(constraints.disableautoplay),
    'user.disablescrollblocking': toBoolean(constraints.disableScrollBlocking),
    clickUrl: getClickUrl(clickUrl, {
      cacheBuster: buster(),
      impressionId,
    }),
    ...(constraints.batchTrackers
      ? { dataURIsEnabled: 1, _batchTrackers: 1 }
      : {}),
    ...(gdpr_consent
      ? getGDPRConsentMacros(gdpr_consent, creative.consentIds)
      : {}),
    ...(gdpr
      ? { [`${GDPR_PREFIX}${encodeURIComponent(btoa(`\${GDPR}`))}`]: gdpr }
      : {}),
    ...(coppa ? { coppa } : {}),
  };

  return deepMerge<typeof celtraMacros>({}, macros, celtraMacros);
};

export const buildCreativeMarkup = (
  macros: ReturnType<typeof getCeltraMacros>,
  tag: Tag,
  markupTemplates: ReturnType<typeof getMarkupTemplates>,
) => {
  const { domain } = getEnvironment();
  const { version } = getOptions();
  const { payload, pageOptions, tagId } = tag;
  const {
    impressionId,
    mobkoi: { campaignId },
  } = payload!;
  const { markup, placementId } = payload!.creative;

  const markupWithInsertedMacros = insertAtSubstring(
    markup,
    'params.clientTimestamp',
    'params=Object.assign(params, ' +
      quoteToApostrophe(JSON.stringify(macros)) +
      ');\n',
  );

  const containerWindow = tag.container!.ownerDocument!.defaultView!;
  const templateCallbackName = addGlobalCallback<TemplateCallback>(
    containerWindow,
    (event) => {
      // "tmpl-load", "tmpl-load-err", "tmpl-load-timeout"
      const reason = `tmpl-load${
        ['error', 'timeout'].includes(event.type) ? `-${event.type}` : ''
      }`.replace('error', 'err');

      if (event.type === 'error') {
        tag.destroy();
      }

      log({
        reason,
        impressionId,
        tagId,
        campaignId: campaignId,
        error: event.type === 'error' ? event.stack || event : undefined,
        celtraPlacementId: placementId,
      });
    },
  );

  /**
   * Called when we load the creative to the page. If the creative has loaded
   * successfully then this callback will error as addGlobalCallback automatically
   * cleans itself up after it's called (templateCallback can only be called once)
   */
  const templateTimeoutTrigger = () => {
    setTimeout(() => {
      try {
        containerWindow[templateCallbackName as keyof typeof containerWindow]({
          type: 'timeout',
        });
      } catch (e) {}
    }, TMPL_TIMEOUT_MS);
  };

  const visualMarkup = insertAtSubstring(
    markupWithInsertedMacros,
    'req.src',
    `req.onerror=req.onload=function(e) {
    try {
      return (window.${templateCallbackName} || window.frameElement?.contentWindow.${templateCallbackName})(e);
    } catch(x) {
      let u = 'https://${
        pageOptions.endpoint
      }/log/${tagId}/?impressionId=${impressionId}&campaign=${campaignId}&v=${version}&domain=${encodeURIComponent(
      domain,
    )}&celtraPlacementId=${placementId}&reason=tmpl-load' + (e && e.type === 'error' ? '-err&error=encodeURIComponent(JSON.stringify(e, Object.getOwnPropertyNames(e)))' : '') + '&detail=nocbfn';
      if (!navigator.sendBeacon || !navigator.sendBeacon(u)) { (new Image()).src = u; }
    }
  };\n`,
  );

  // creates an invisible iframe on the page
  const rtbMarkup = createIframeSrcdoc({
    clazz: 'mbk-griffin',
    width: '300px',
    height: '250px',
    markup: tag.payload!.rtb?.markup || '',
    hide: true,
  });

  return {
    ...markupTemplates,
    html: [...markupTemplates.html, visualMarkup],
    children: [...markupTemplates.children, rtbMarkup],
    triggers: [...markupTemplates.triggers, templateTimeoutTrigger],
  };
};

export const buildCeltraClickTrackers = (tag: Tag) => {
  const { pageOptions, payload } = tag;
  const clickTrackers: {
    url: string;
    type: 'script' | 'pixel' | 'url' | 'redirect';
  }[] = [];

  if (payload?.rtb) {
    const data = extractCreativeData(payload.rtb.markup);
    if (data && data.clickUrl) {
      clickTrackers.push({
        url: data.clickUrl,
        type: data.clickUrlType || 'pixel',
      });
    }
  }

  if (RE_URL_TEST.test(pageOptions.clickUrl)) {
    clickTrackers.push({
      url: pageOptions.clickUrl,
      type: 'script',
    });
  }

  // handle click trackers from the payload
  payload?.clickTrackers?.forEach((tracker) => {
    clickTrackers.push({
      url: tracker.tracker,
      type: tracker.type,
    });
  });

  return clickTrackers.map((tracker) => ({
    ...tracker,
    url: processMacros(
      {
        cacheBuster: buster(),
        impressionId: payload!.impressionId,
      },
      tracker.url,
    ),
  }));
};

// Here lives the params that we have possibly for query string params
// We need to extend this for any new params that we want to support on case by case basis
// e.g. http://example.com?&adurl=http://bbc.com
const clickTrackerParams = ['adurl'];

/**
 * If we have multiple redirect trackers then we need to chain/nest them together with
 * each urls query params being encoded and passed to the next redirect url
 * We're likely to only have one redirect here, but we should support multiple just incase
 */
const redirectChainBuilder = (
  trackers: { url: string; type: 'script' | 'pixel' | 'url' | 'redirect' }[],
  tag: Tag,
) => {
  const redirects = trackers
    .filter(({ type }) => type === 'redirect')
    .map(({ url }) => url);

  // Return a curried function that will build the URL correctly when called with the final landing url
  return (landingUrl: string) => {
    return [...redirects, landingUrl].reverse().reduce((acc, url, index) => {
      // this is the "last" (we've reversed the array) url and should remain untouched
      if (index === 0) {
        return url;
      }

      const urlObj = new URL(url);
      // check if the redirect url has any of the clickTrackerParams, return first match
      const redirectParam = clickTrackerParams.find((param) =>
        urlObj.searchParams.has(param),
      );

      // if we don't have a redirect param, log an error and return the original url
      // this probably means that we need to add a new param to the clickTrackerParams array
      if (!redirectParam) {
        logError({
          reason: 'click-tracker-error',
          message: `Redirect URL does not contain a known click tracker param ${url}`,
          tagId: tag.tagId,
          impressionId: tag.payload!.impressionId,
        });
        return acc;
      }

      // update the search params with the redirect param
      urlObj.searchParams.set(redirectParam, acc);

      // return the updated url. Note: encoding is automatically handled by the URL object
      return urlObj.toString();
    }, '');
  };
};

/**
 * For each creative click tracker that exist in the overrides, we need to append it
 * to the end of the redirect url chain so that ultimately we call that URL
 */
const getNewCreativeClickTrackers = (
  urlOpenedOverrideUrls: { [key: string]: string } = {},
  redirectBuilder: ReturnType<typeof redirectChainBuilder>,
) =>
  Object.entries({
    ...urlOpenedOverrideUrls,
  }).reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: redirectBuilder(value),
    };
  }, {});

/**
 * To append trackers to the celtra creative, we need to find the iframe
 * with the celtra creative running inside of it. To do that we listen for
 * mutations within the tag container and grab the iframes within.
 * We then search each iframe for the celtra creative instance and register
 * an event handler to the celtra api. Once that triggers, we drop/call
 * the trackers based on their type
 */
export const attachCeltraClickTrackers = (
  tag: Tag,
  trackers: { url: string; type: 'script' | 'pixel' | 'url' | 'redirect' }[],
) => {
  if (!trackers.length) {
    return;
  }

  const iframes: HTMLIFrameElement[] = [];
  let cancelWaitUntil: (() => void) | null = null;
  const observer = new MutationObserver((mutationsList) => {
    mutationsList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        mutation.addedNodes.forEach((node) => {
          if (isIframe(node) && !node.src) {
            iframes.push(node);
          }
        });
      }
    });
    if (cancelWaitUntil || !iframes.length) {
      return;
    }
    cancelWaitUntil = waitUntil(
      () => iframes.find((e) => e.contentWindow?.creative),
      (iframe) => {
        if (!iframe) {
          return;
        }

        const creative = iframe.contentWindow?.creative;
        observer.disconnect();
        if (creative && creative.once) {
          const buildUrl = redirectChainBuilder(trackers, tag);
          const newUrlOpenedOverrides = getNewCreativeClickTrackers(
            creative.urlOpenedOverrideUrls,
            buildUrl,
          );

          // This handles redirects
          // We replace the urlOpenedOverrideUrls with the concatenated URLs which ultimately resolve
          // to the original urlOpenedOverrideUrls - we just need to ensure that all the click trackers
          // are called in the chain and the final click trackers are those that have been setup for each
          // creative area (e.g. if the unit has multiple hotspot trackers)
          creative.urlOpenedOverrideUrls = newUrlOpenedOverrides;

          creative.once('clickThrough', (evt) => {
            trackers.forEach((t) => {
              if (t.type === 'url' || t.type === 'pixel') {
                // We will use beaconImage here rather than sendBeacon as it assumes that the endpoint
                // can accept POST requests which not all our providers support, fallback to using an image
                beaconImage(t.url);
              }

              if (t.type === 'script') {
                loadScript(t.url);
              }
            });
          });
        }
      },
    );
  });

  observer.observe(tag.container!, {
    childList: true,
    subtree: true,
  });

  // If tag is destroyed, we need to cleanup timers and the observer
  tag.cleanupFunctions.push(() => {
    if (observer.disconnect) {
      observer.disconnect();
    }
    if (cancelWaitUntil) {
      cancelWaitUntil();
    }
  });
};

export const buildCeltraCreative$ = (
  tag: Tag,
  markupTemplates: ReturnType<typeof getMarkupTemplates>,
) =>
  pipe(
    fromValue(tag),
    map((tag) =>
      buildCreativeMarkup(getCeltraMacros(tag), tag, markupTemplates),
    ),
  );
